import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, Center, Divider, Link} from '@chakra-ui/react';
import {TextG10, TextG12} from '../../components/typography';
import {BlueButtonFixedWidth, WhiteButtonFixedWidth} from '../../components/button';
import {fetchNumOfRegisteredUsers} from '../../endpoints/api';
import LandingWrapper from '../../components/LandingWrapper';
import useAuth from "../../hooks/useAuth";
import {logAnalyticsEvent} from "../../utils/analytics";
import {ToastMessage} from "../../components/toast";
import {isIOS} from "../../utils/deviceCheck";

export const IS_COOKIE_ACCEPTED = 'is_cookie_accepted';

function Auth() {
  const navigate = useNavigate();
  const [numOfRegisteredUsers, setNumOfRegisteredUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if current date is before June 8th
  const isBeforeJune8th = () => {
    const now = new Date();
    const june8th = new Date(now.getFullYear(), 5, 8); // Month is 0-indexed, so 5 = June
    return now < june8th;
  };

  const googleSignupDivRef = useRef<HTMLDivElement | null>(null);
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const { socialLogin } = useAuth();
  const GOOGLE_PROVIDER = 'Google';

  useEffect(() => {
    fetchNumOfRegisteredUsers().then(({data: {count}}) => setNumOfRegisteredUsers(count));

    // if (!isCookieAccepted) {
    //   dispatch(BottomSheetNotificationActions.pushNotification({title: 'cookie'}));
    // }
    const loadGoogleScript = (): Promise<void> =>
        new Promise((resolve) => {
          if ((window as any).google?.accounts?.id) return resolve();
          const s = document.createElement('script');
          s.src = 'https://accounts.google.com/gsi/client';
          s.onload = () => resolve();
          document.head.appendChild(s);
        });

    if (!GOOGLE_CLIENT_ID || isIOS) return;

    loadGoogleScript()
        .then(() => {
          const g = (window as any).google;
          if (!g?.accounts?.id) return;

          // initialize Google Identity
          g.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleSignupCredentialResponse,
          });

          if (googleSignupDivRef.current) {
            g.accounts.id.renderButton(googleSignupDivRef.current, {
              theme: 'outline',
              size: 'large',
              width: 260,
              text: 'signup_with'
            });
          }

          // optionally prompt One-Tap only on larger screens
          if (window.innerWidth > 640) {
            g.accounts.id.prompt();
          }
        })
        .catch((err) => {
          console.error('Google Identity script failed to load', err);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);


  const handleGoogleSignupCredentialResponse = (response: any) => {
    const idToken = response?.credential;
    if (!idToken) return;

    setIsLoading(true);
    socialLogin(GOOGLE_PROVIDER, idToken)
        .then((redirect) => {
            logAnalyticsEvent('login', { method: GOOGLE_PROVIDER });
            navigate(redirect ?? '/home');
            setIsLoading(false);
        })
        .catch((e: any) => {
            setIsLoading(false);
            ToastMessage({ status: 'error', text: e.message });
        });
  }

  return (
    <LandingWrapper
      title={'Get Out There!'}
      subtitle={'Experience more and love where you live.'}
      numOfRegistered={numOfRegisteredUsers}
      //additionalTitleLine={isBeforeJune8th() ? '(or Just Navigate Music Fest)' : undefined}
    >
      <Box mt={8} w={'100%'}>
        <Center>
          <BlueButtonFixedWidth onClick={() => navigate('/login')}>Login</BlueButtonFixedWidth>
        </Center>
        <Center px={6} py={5}>
          <Divider />
          <TextG10 color="white" w="100%" textAlign="center">
            New member?
          </TextG10>
          <Divider />
        </Center>
        <Center flexDir="column" gap={1}>
          <WhiteButtonFixedWidth px={4} onClick={() => navigate('/onboarding')}>
            Sign up now
          </WhiteButtonFixedWidth>
          <Center px={6} py={2} w="100%">
            <TextG12 color="white" w="100%" textAlign="center">
              OR
            </TextG12>
          </Center>
          <Center mt={0} mb={3}>
            <Link
              textDecoration={'underline'}
              color="white"
              onClick={() => {
                navigate('/invite-code');
              }}>
              I Have an Invite Code
            </Link>
          </Center>
          {!isIOS && (
            <>
              <Center px={6} py={2} w="100%">
                <TextG12 color="white" w="100%" textAlign="center">
                  OR
                </TextG12>
              </Center>
              <Center mt={2} mb={2}>
                <div ref={googleSignupDivRef as any} />
              </Center>
            </>
          )}
        </Center>
      </Box>
    </LandingWrapper>
  );
}

export default Auth;
